import type { PropsWithChildren } from "react"
import React, { useEffect } from "react"
import fetch from "isomorphic-fetch"
import {
    ApolloProvider as Provider,
    ApolloClient,
    ApolloLink,
    HttpLink,
    InMemoryCache,
    type TypePolicies,
} from "@apollo/client"
import { setContext } from "@apollo/client/link/context"

import { CustomHeaders, CLIENT_TYPE } from "~config/constants"
import { createSentryErrorLink, createSentryTimingLink } from "~utils/sentry"
import { getApiUrl, getVersionNumber } from "~utils/helpers/helpers"
import { getAccessTokenFuncVar } from "~utils/hooks/use-auth"
import {
    consumeBridgeTimeoutFlag,
    getAccessTokenFromBridge,
    isInAppMode,
    subscribeToNativeAuthToken,
} from "~utils/native-auth-bridge"

export default function ApolloProvider({
    children,
}: Readonly<PropsWithChildren>) {
    const version = getVersionNumber()
    const apiUri = getApiUrl()
    const httpLink = new HttpLink({
        uri: apiUri,
        fetch,
        headers: {
            [CustomHeaders.CLIENT_NAME]: CLIENT_TYPE,
            [CustomHeaders.CLIENT_VERSION]: version,
        },
    })

    const authLink = setContext(async (_, { headers }) => {
        // Calling getAccessTokenSilently every time we need the access token
        // so that auth0 can handle token refreshes.
        // See explanation in use-auth.ts for why we're using a reactive var.
        // In in-app mode, fall back to the native bridge directly: queries
        // can fire before useAuth's mount effect has stored the getter.
        const getAccessToken =
            getAccessTokenFuncVar() ??
            (isInAppMode() ? getAccessTokenFromBridge : null)
        const accessToken = await getAccessToken?.()

        return {
            headers: {
                ...headers,
                authorization: accessToken ? `Bearer ${accessToken}` : "",
            },
        }
    })

    const link = ApolloLink.from([
        createSentryTimingLink(apiUri),
        createSentryErrorLink(apiUri),
        authLink,
        httpLink,
    ])

    const client = new ApolloClient({
        name: CLIENT_TYPE,
        version: version,
        link,
        cache: new InMemoryCache({ typePolicies: getTypePolicies() }),
        credentials: "include",
    })

    useEffect(() => subscribeToBridgeTimeoutRecovery(client), [])

    return <Provider client={client}>{children}</Provider>
}

/**
 * In-app mode self-healing: queries that time out waiting for the first
 * native token push land in a terminal error state. When a token finally
 * arrives, refetch them so the page recovers without a manual reload.
 * Returns an unsubscribe function.
 */
export function subscribeToBridgeTimeoutRecovery(
    client: RefetchableClient
): () => void {
    if (!isInAppMode()) return noopUnsubscribe

    return subscribeToNativeAuthToken(() => {
        if (!consumeBridgeTimeoutFlag()) return

        void client.reFetchObservableQueries()
    })
}

export function getTypePolicies(): TypePolicies {
    return {
        // Quote add ons don't have globally unique IDs, they use the same
        // ID as the referenced add on. In order to cache properly, apollo
        // needs to use a combination of id and package_id.
        QuoteAddOn: {
            keyFields: ["id", "package_id"],
        },
        QuickBooksItemMapping: {
            keyFields: ["entity_id"],
        },
    }
}

function noopUnsubscribe(): void {
    // Nothing to clean up outside in-app mode.
}

interface RefetchableClient {
    reFetchObservableQueries: () => Promise<unknown>
}
